import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import { categories, getMoviesByCategory, type MovieItem } from '@/mocks/movies';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeSlug, setActiveSlug] = useState(slug || 'movie');
  const [items, setItems] = useState<MovieItem[]>([]);

  useEffect(() => {
    if (slug && categories.some((c) => c.slug === slug)) {
      setActiveSlug(slug);
    }
  }, [slug]);

  useEffect(() => {
    setItems(getMoviesByCategory(activeSlug));
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [activeSlug]);

  const handleTabChange = (newSlug: string) => {
    setActiveSlug(newSlug);
    navigate(`/category/${newSlug}`, { replace: true });
  };

  const currentCategory = categories.find((c) => c.slug === activeSlug);
  const categoryName = currentCategory?.name || '分类';

  const breadcrumbItems = [
    { '@type': 'ListItem' as const, position: 1, name: '首页', item: 'https://www.90yingshi.com' },
    { '@type': 'ListItem' as const, position: 2, name: categoryName, item: `https://www.90yingshi.com/category/${activeSlug}` },
  ];

  return (
    <>
      <SEOHead
        title={`${categoryName}分类 - 高清${categoryName}在线观看`}
        description={`90影视${categoryName}频道，海量${categoryName}资源在线观看，高清画质不缓冲，每日更新最新${categoryName}，免费无广告畅享观影体验。`}
        keywords={`90影视,${categoryName},免费,在线观看,高清,不缓冲,无广告`}
        canonicalUrl={`https://www.90yingshi.com/category/${activeSlug}`}
      />
      <SchemaOrg type="Organization" />
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${categoryName} | 90影视`,
          description: `90影视${categoryName}频道高清在线观看`,
          url: `https://www.90yingshi.com/category/${activeSlug}`,
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbItems,
          },
        }}
      />
      <SchemaOrg type="BreadcrumbList" data={{ items: breadcrumbItems }} />

      <main className="min-h-screen pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-50 font-heading mb-6">
              {categoryName}
            </h1>

            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleTabChange(cat.slug)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-200/60 text-foreground-400 hover:text-foreground-200 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-base`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
          <div className="flex items-center justify-between mb-6">
            <p className="text-foreground-500 text-sm">
              共找到 <strong className="text-foreground-200">{items.length}</strong> 部{categoryName}资源
            </p>
          </div>

          {items.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
              {items.map((item) => (
                <Link
                  key={item.id}
                  to={`/article/${item.id}`}
                  className="group relative overflow-hidden rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-500/30 transition-all duration-300 cursor-pointer"
                >
                  <div className="aspect-[2/3] overflow-hidden">
                    <img
                      src={item.imageUrl}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-2 right-2 bg-background-950/80 backdrop-blur-sm rounded-md px-2 py-0.5 flex items-center gap-1">
                      <i className="ri-star-fill text-accent-400 text-xs"></i>
                      <span className="text-accent-400 text-xs font-semibold">{item.rating}</span>
                    </div>
                    {item.isNew && (
                      <div className="absolute top-2 left-2 bg-primary-500 text-background-50 text-xs px-2 py-0.5 rounded-md font-medium">
                        NEW
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <h3 className="text-foreground-100 text-sm font-medium truncate font-heading group-hover:text-primary-400 transition-colors">
                      {item.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-foreground-600 text-xs">{item.year}</span>
                      <span className="text-foreground-700 text-xs">·</span>
                      <span className="text-foreground-600 text-xs truncate">{item.region}</span>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {item.tags.slice(0, 2).map((tag) => (
                        <span key={tag} className="text-foreground-700 text-xs bg-background-200/60 px-1.5 py-0.5 rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 bg-background-200 rounded-2xl flex items-center justify-center">
                <i className="ri-emotion-sad-line text-2xl text-foreground-500"></i>
              </div>
              <p className="text-foreground-500 text-sm">暂无{categoryName}资源，请稍后再来</p>
            </div>
          )}
        </div>
      </main>
    </>
  );
}